import React, { useState } from "react";
import {
  FormControl,
  Select,
  MenuItem,
  ListItemText,
  Checkbox,
  IconButton,
  useMediaQuery,
} from "@mui/material";
import { ExpandMore, ExpandLess } from "@mui/icons-material";
import { useTheme, makeStyles } from "@mui/styles";
import { FormattedMessage, useIntl } from "react-intl";

const SelectCategory = ({
  handleInputValue,
  variant,
  labelId,
  required,
  className = "",
  multiple = false,
  list = false,
  size,
  value = [],
  items = [],
  ...props
}) => {
  const intl = useIntl();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));

  /* ─────────────────────────  styling identical to SelectRegion */
  const useStyles = makeStyles(() => ({
    desktopSelect: {
      border: 0,
      "& fieldset": { border: 0 },
    },
    mobileSelect: { borderRadius: "20px !important" },
    menuItem: {
      height: isDesktop ? 36 : 32,
      padding: isDesktop ? 0 : 16,
      "&.Mui-selected": { backgroundColor: "white !important" },
      "&:hover": {
        backgroundColor: `${theme.palette.common.sh_lightgrey} !important`,
      },
    },
    menuItemOption: { paddingLeft: "32px !important" },
  }));
  const classes = useStyles();

  /* ─────────────────────────  build industry → category map */
  const industries = React.useMemo(() => {
    const grouped = items.reduce((unit, category) => {
      (unit[category.industry] = unit[category.industry] || []).push(category.code);
      return unit;
    }, {});
    return Object.keys(grouped).map((key) => ({
      name: key,
      categories: grouped[key],
      isOpened: true,
    }));
  }, [items]);

  /* store expanded / collapsed state per industry */
  const [openMap, setOpenMap] = useState(
    industries.reduce((open, item) => ({ ...open, [item.name]: true }), {})
  );

  const toggleIndustry = (end, industry) => {
    end.stopPropagation();
    setOpenMap((prev) => ({ ...prev, [industry]: !prev[industry] }));
  };

  const isAllSelected = (categories) => categories.every((c) => value.includes(c));
  const areSomeSelected = (categories) =>
    !isAllSelected(categories) && categories.some((c) => value.includes(c));

  const updateFromArray = (newArr) =>
    handleInputValue([...new Set(newArr)]);

  const handleChange = (e) => {
    const values = e.target.value;
    applySelection(values);
  };

  const handleItemClick = (codeOrGroup) => {
    if (codeOrGroup.startsWith("group-")) {
      const industry = codeOrGroup.slice(6);
      const categories = industries.find((i) => i.name === industry).categories;
      const newVals = isAllSelected(categories)
        ? value.filter((value) => !categories.includes(value))
        : value.concat(categories);
      updateFromArray(newVals);
    } else {
      const newVals = value.includes(codeOrGroup)
        ? value.filter((value) => value !== codeOrGroup)
        : value.concat(codeOrGroup);
      updateFromArray(newVals);
    }
  };

  const applySelection = (array) => {
    const groupClicks = array.filter((value) => value.startsWith("group-"));
    let   plain = array.filter((value) => !value.startsWith("group-"));

    groupClicks.forEach((groupValue) => {
      const industryKey = groupValue.slice(6);
      const categories  = industries.find((industry) => industry.name === industryKey)
        ?.categories || [];

      plain = isAllSelected(categories)
        ? plain.filter((v) => !categories.includes(v))
        : plain.concat(categories);
    });

    updateFromArray(plain);
  };

  /* ──────────────────────────  RENDER  */
  const renderMenu = (modeSelect = false) => (
    <>
      {industries.map((industry) => (
        <React.Fragment key={industry.name}>
          <MenuItem
            value={`group-${industry.name}`}
            onClick={() => !modeSelect && handleItemClick(`group-${industry.name}`)}
            className={`${classes.menuItem} ${classes.menuItemHeader}`}
            style={{ display: "flex" }}
          >
            <Checkbox
              checked={isAllSelected(industry.categories)}
              indeterminate={areSomeSelected(industry.categories)}
            />
            <ListItemText
              primary={intl.formatMessage({
                id: `company.company_industry.${industry.name}`,
                defaultMessage: industry.name,
              })}
            />
            <IconButton
              size="small"
              edge="start"
              color="inherit"
              onClick={(e) => toggleIndustry(e, industry.name)}
            >
              {openMap[industry.name] ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
          </MenuItem>

          {/* category options */}
          {openMap[industry.name] &&
            industry.categories.map((category) => (
              <MenuItem
                key={category}
                value={category}
                onClick={() => !modeSelect && handleItemClick(category)}
                className={`${classes.menuItem} ${classes.menuItemOption}`}
              >
                <Checkbox checked={value.includes(category)} />
                <ListItemText
                  primary={intl.formatMessage({
                    id: `categories.${category}`,
                  })}
                />
              </MenuItem>
            ))}
        </React.Fragment>
      ))}
    </>
  );

  if (!list) {
    return (
      <FormControl
        fullWidth
        required={required}
        variant={variant}
        className={className}
        size={size}
      >
        <Select
          {...props}
          multiple={multiple}
          value={value}
          onBlur={handleChange}
          onChange={handleChange}
          label={<FormattedMessage id={labelId} />}
          labelId={labelId}
          MenuProps={{ disableScrollLock: true, autoFocus: false }}
          className={isDesktop ? classes.desktopSelect : classes.mobileSelect}
          IconComponent={() => null}
          inputProps={{ style: { padding: 0 } }}
        >
          {renderMenu(true)}
        </Select>
      </FormControl>
    );
  }

  return renderMenu(false);
};

export default SelectCategory;
